import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid2'
import CaleoInputLabel from 'Components/reusable/CaleoCustomComponents/CaleoInputLabel'
import React, { ComponentPropsWithRef } from 'react'
import { MULTILINE_TEXT_FIELD_MAX_LENGTH } from 'constants/caleo-constants'
import { useTranslation } from 'react-i18next'

interface OwnProps {
  className?: string
  error?: string | false
  fullWidth?: boolean
  grow?: boolean
  helperText?: string | null
  label?: React.ReactNode
  required?: boolean
  style?: React.CSSProperties
  backendError?: string
  icon?: React.ReactElement
  id?: string
  type?: string
  multiline?: boolean
  autosize?: boolean
  insetLabel?: boolean
  endAdornment?: React.ReactNode
  min?: number
  max?: number
  ariaLabel?: string
  ariaLabelledBy?: string
}

/**
 * Input field component.
 *
 * @param className - {string} Class name for styling the component.
 * @param error - {string | false} Error message.
 * @param fullWidth - {boolean} Should component be full width.
 * @param grow - {boolean} Should component grow.
 * @param helperText - {string | null} Helper text.
 * @param label - {React.ReactNode} Label text.
 * @param required - {boolean} Is the component required.
 * @param style - {React.CSSProperties} Style parameter of the component.
 * @param backendError - {string} Backend request error message.
 * @param icon - {React.ReactElement} Icon element.
 * @param id - {string} Element ID.
 * @param type - {string} Type.
 * @param multiline - {boolean} Is field multiline.
 * @param autosize - {boolean} Should the component resize automatically.
 * @param insetLabel - {boolean} Should the label be inset.
 * @returns {React.ReactElement} - Input field component.
 */
export const InputField: React.FC<OwnProps & Omit<ComponentPropsWithRef<typeof TextField>, keyof OwnProps>> = ({
  className,
  error,
  fullWidth,
  grow,
  helperText,
  label,
  required,
  style: styleProp = {},
  backendError,
  icon,
  id,
  type,
  multiline,
  autosize,
  insetLabel,
  endAdornment,
  min,
  max,
  ariaLabel,
  ariaLabelledBy,
  ref,
  ...rest
}) => {
  const { t } = useTranslation()
  const generatedId = React.useId()
  const resolvedName = typeof rest.name === 'string' ? rest.name : undefined
  const resolvedId = id ?? generatedId
  const normalizedIdentifier = (id ?? resolvedName)?.replace(/[^a-zA-Z0-9_-]+/g, '-')
  const helperTextId = normalizedIdentifier ? `field-${normalizedIdentifier}-helper-text` : undefined

  const mergedHtmlInputSlotProps = {
    ...(rest.slotProps?.htmlInput ?? {}),
    ...(multiline && { maxLength: MULTILINE_TEXT_FIELD_MAX_LENGTH }),
    ...(multiline && autosize && { style: { resize: 'vertical' } }),
    ...(min !== undefined && { min }),
    ...(max !== undefined && { max }),
    ...(ariaLabel && { 'aria-label': ariaLabel }),
    ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
    ...((error || helperText) && helperTextId && { 'aria-describedby': helperTextId }),
    ...(required && { 'aria-required': true }),
    ...(!!error && { 'aria-invalid': true }),
  } as Record<string, unknown>

  const mergedInputLabelSlotProps = {
    ...(rest.slotProps?.inputLabel ?? {}),
    ...(insetLabel && { shrink: true }),
  }

  const mergedFormHelperTextSlotProps = {
    ...(rest.slotProps?.formHelperText ?? {}),
    ...(helperTextId && { id: helperTextId }),
  }

  const mergedInputSlotProps = {
    ...(rest.slotProps?.input ?? {}),
    ...(endAdornment && { endAdornment }),
  }

  const style = {
    ...styleProp,
    width: fullWidth ? '100%' : undefined,
    flexGrow: grow ? 1 : undefined,
  }

  if (backendError) {
    error = backendError
  }

  // Attach data-testid automatically if id or name is provided.
  const testId = normalizedIdentifier ? `field-${normalizedIdentifier}` : undefined

  return (
    <Grid style={style} ref={ref}>
      {!insetLabel && <CaleoInputLabel htmlFor={resolvedId} icon={icon} required={required} label={label} />}
      <TextField
        id={resolvedId}
        margin="dense"
        error={!!error}
        className={`${className}`}
        data-testid={testId}
        label={
          insetLabel ? (
            <Grid pr={label && typeof label === 'string' && label.length > 15 ? 1.5 : 0.4}>
              {label}
              {required && (
                <Typography color="#BE3C3C" fontSize="0.85em" fontWeight="bold" component="span" pl={0.5} pr={0.5}>
                  {required && `${t('required')}`}
                </Typography>
              )}
            </Grid>
          ) : undefined
        }
        variant="outlined"
        helperText={error ? error : helperText}
        type={type}
        multiline={multiline}
        size="small"
        sx={{
          '& .MuiFormHelperText-root': {
            textAlign: 'right',
          },
          ...(rest.sx ?? {}),
        }}
        {...rest}
        slotProps={{
          ...rest.slotProps,
          htmlInput: mergedHtmlInputSlotProps,
          inputLabel: mergedInputLabelSlotProps,
          formHelperText: mergedFormHelperTextSlotProps,
          input: mergedInputSlotProps,
        }}
        fullWidth={fullWidth}
      />
    </Grid>
  )
}
